import _ from 'lodash'
import {
  axiosInstance as axios,
  handleAxiosErrors,
  checkEmptyOnList,
  floorToTwoDecimals,
  breakListIntoChunks,
} from '_utils'
import config from '../store/_config'
import {
  convertDealBuilderListToReact,
  convertMemberNamesToReactSelect,
  convertZonesToReactSelect,
} from '../selectors/converterSelectors'
import {
  buildDealBuilderSubmissionData,
  buildPromotionData,
  updatedComparedDealBuilderSubmissionData,
} from '../selectors/requestSelectors'
import moment from 'moment'
import {
  convertProductsListingToAReadableFormat,
  convertProductsPricesToAReadableFormat,
} from '../selectors/productSelectors'
import { createAlert } from '../dom/window'
import {
  axiosRequestProductList,
  updateFullProductsList,
  requestDiscountedProductsPrices,
  getSupplierCustomerPromo,
} from './productsSupplierActions'
import { axiosRequestProcessedProductsPrices } from './productsActions'
import { triggerDiscount, setDiscountSyncing } from './userActions'
import { captureSentryEvent } from './sentryActions'

/**
 * Toggle Loading
 *
 * @param key
 * @param value
 * @returns {{type: string, value: *, key: *}}
 */
export const toggleLoading = (key, value) => {
  return {
    type: 'TOGGLE_DEAL_BUILDER_LOADING',
    key: key,
    value: value,
  }
}

/**
 * Set Deal Builder List
 *
 * @param value
 * @returns {{type: string, value: *}}
 */
export const setDealBuilderList = (value) => {
  return {
    type: 'SET_DEAL_BUILDER_LIST',
    value: value,
  }
}

export const setPromoDealsSync = (value) => {
  return {
    type: 'SIGNAL_PROMODEAL_COMPLETE',
    value: value,
  }
}

export const ADD_REMOVAL_ITEM = 'ADD_REMOVAL_ITEM'
export const REMOVE_REMOVAL_ITEM = 'REMOVE_REMOVAL_ITEM'

export const addRemovalItem = (item) => ({
  type: ADD_REMOVAL_ITEM,
  payload: item,
})

export const removeRemovalItem = (id) => ({
  type: REMOVE_REMOVAL_ITEM,
  payload: id,
})

/**
 * Update Deal Builder Form Fields
 *
 * @param key
 * @param value
 * @returns {{type: string, value: *, key: *}}
 */
export const updateDealBuilderDetails = (key, value) => {
  return {
    type: 'UPDATE_DEAL_BUILDER_DETAILS',
    key: key,
    value: value,
  }
}

/**
 * Update Deal Builder modified row
 *
 * @param rowData
 * @param id
 * @returns {{type: string, value: *, key: *}}
 */
export const updateDealBuilderModifiedRow = (row, key, value, previousValue) => {
  return (dispatch, getState) => {
    if (value === previousValue) {
      return
    }

    dispatch({
      type: 'PUSH_SENTRY_EVENT',
      value: {
        category: 'ui.input',
        message: `Deal Builder Modified Product ${row?.original?.productID || 0}-${key}: ${previousValue} - ${value}`,
        level: 'info',
        timestamp: Math.floor(Date.now() / 1000),
        data: {
          key: key,
          value: value,
          previousValue: previousValue,
        },
      },
    })
    /**
     * lastKey                      the key that was last updated (percentage, flat, case price)
     *      flat_discount
     *      percentage_discount
     *      case_price
     * lastValue                    the value that was last entered
     * flatDiscountValue            calculated value for flat discount
     * percentageDiscountValue      calculated value for percentage discount
     * casePriceValue               calculated value for case price
     */
    const originalData = row.original

    const {
      id,
      productID,
      sell_by_case,
      qty_per_case,
      case_price,
      unit_price,
      wetTax,
      case_discount,
      frontline_case_price,
      wholesale_unit_per_case,
    } = originalData

    const originalCasePrice = typeof case_price == 'string' ? parseFloat(case_price) : case_price
    const originalUnitPrice = typeof unit_price == 'string' ? parseFloat(unit_price) : unit_price

    const frontlineCasePrice =
      typeof frontline_case_price == 'string' ? parseFloat(frontline_case_price) : frontline_case_price

    // Don't do anything if original case price is 0, just in case...
    if (Number(originalCasePrice) === 0) {
      return
    }

    let modifiedData = {
      lastKey: key,
      lastValue: value,
      flatDiscountValue: '',
      percentageDiscountValue: '',
      casePriceValue: '',
      unitPriceValue: '',
      originalCasePrice: originalCasePrice,
      originalUnitPrice: floorToTwoDecimals(originalUnitPrice / wholesale_unit_per_case),
      productID: productID,
      modified: true,
    }
    let percentageInitialCalculation

    if (!isNaN(value) || value !== '') {
      // Calculate each of them
      switch (key) {
        case 'flat_discount':
          if (value > originalCasePrice) {
            value = originalCasePrice
          }

          modifiedData.flatDiscountValue = value

          percentageInitialCalculation = modifiedData.flatDiscountValue
          modifiedData.percentageDiscountValue =
            percentageInitialCalculation > 0 ? (percentageInitialCalculation / frontlineCasePrice) * 100 : 0

          //Removed wetTax from falt discount
          modifiedData.casePriceValue = floorToTwoDecimals(
            (originalCasePrice / (1 + wetTax) - modifiedData.flatDiscountValue) * (1 + wetTax)
          )

          if (!sell_by_case) {
            modifiedData.unitPriceValue =
              modifiedData.casePriceValue > 0
                ? floorToTwoDecimals(modifiedData.casePriceValue / wholesale_unit_per_case)
                : 0
          }

          break

        case 'percentage_discount':
          if (value > 100) {
            value = 100
          }

          modifiedData.percentageDiscountValue = value
          modifiedData.flatDiscountValue = floorToTwoDecimals((frontlineCasePrice * value) / 100)

          //Removed wetTax from percentage discount
          modifiedData.casePriceValue = floorToTwoDecimals(
            (originalCasePrice / (1 + wetTax) - modifiedData.flatDiscountValue) * (1 + wetTax)
          )

          if (!sell_by_case) {
            modifiedData.unitPriceValue =
              modifiedData.casePriceValue > 0
                ? floorToTwoDecimals(modifiedData.casePriceValue / wholesale_unit_per_case)
                : 0
          }
          break

        case 'case_price':
          if (value > originalCasePrice) {
            value = originalCasePrice
          }

          modifiedData.casePriceValue = value

          //Calculate discount on original case price excluding wetTax
          modifiedData.flatDiscountValue = floorToTwoDecimals(
            case_price / (1 + wetTax) - modifiedData.casePriceValue / (1 + wetTax)
          )

          percentageInitialCalculation = floorToTwoDecimals(modifiedData.flatDiscountValue)
          modifiedData.percentageDiscountValue =
            percentageInitialCalculation > 0 ? (percentageInitialCalculation / frontlineCasePrice) * 100 : 0

          if (!sell_by_case) {
            modifiedData.unitPriceValue =
              modifiedData.casePriceValue > 0
                ? floorToTwoDecimals(modifiedData.casePriceValue / wholesale_unit_per_case)
                : 0
          }
          break

        case 'unit_price':
          if (value > originalUnitPrice) {
            value = originalUnitPrice
          }

          modifiedData.unitPriceValue = value
          modifiedData.casePriceValue = floorToTwoDecimals(value * wholesale_unit_per_case)

          //Calculate discount on original case price excluding wetTax
          modifiedData.flatDiscountValue = floorToTwoDecimals(
            case_price / (1 + wetTax) - modifiedData.casePriceValue / (1 + wetTax)
          )

          percentageInitialCalculation = modifiedData.flatDiscountValue
          modifiedData.percentageDiscountValue =
            percentageInitialCalculation > 0 ? (percentageInitialCalculation / frontlineCasePrice) * 100 : 0

          break
      }

      // Don't allow minus
      if (modifiedData.flatDiscountValue < 0) {
        modifiedData.flatDiscountValue = 0
      }
      if (modifiedData.percentageDiscountValue < 0) {
        modifiedData.percentageDiscountValue = 0
      }
      if (modifiedData.casePriceValue < 0) {
        modifiedData.casePriceValue = 0
      }
      if (modifiedData.unitPriceValue < 0) {
        modifiedData.unitPriceValue = 0
      }

      // Always round them up (if they are not being edited)
      if (key != 'flat_discount') {
        modifiedData.flatDiscountValue = parseFloat(modifiedData.flatDiscountValue).toFixed(2)
      }
      if (key != 'percentage_discount') {
        modifiedData.percentageDiscountValue = parseFloat(modifiedData.percentageDiscountValue).toFixed(2)
      }
      if (key != 'case_price') {
        modifiedData.casePriceValue = parseFloat(modifiedData.casePriceValue).toFixed(2)
      }
      if (key != 'unit_price') {
        if (!sell_by_case) {
          modifiedData.unitPriceValue = parseFloat(modifiedData.unitPriceValue).toFixed(2)
        }
      }
    }

    if (parseFloat(modifiedData?.flatDiscountValue) === case_discount) {
      modifiedData.modified = false
    }

    dispatch({
      type: 'UPDATE_DEAL_BUILDER_DETAILS_ROW_DATA',
      value: modifiedData,
      key: id,
    })
  }
}

/**
 * Update Available Products in redux
 *
 * @param value
 * @returns {{type: string, value: *}}
 */
export const replaceAvailableProducts = (value) => {
  return {
    type: 'REPLACE_DEAL_BUILDER_AVAILABLE_PRODUCTS',
    value: value,
  }
}

export const updateAvailableProducts = (value) => {
  return {
    type: 'UPDATE_DEAL_BUILDER_AVAILABLE_PRODUCTS',
    value: value,
  }
}

export const loadDealBuilderSubmissionDetail = (id) => {
  return (dispatch, getState) => {
    dispatch(toggleLoading('detail', true))

    dispatch({
      type: 'SET_VIEW_DEAL_BUILDER_ID',
      value: id,
    })

    dispatch(toggleLoading('detail', false))
  }
}

export const toggleDeleteDealBuilderSubmissionModal = (toggle) => {
  return (dispatch, getState) => {
    dispatch({
      type: 'TOGGLE_DELETE_BUILDER_SUBMISSION_MODAL',
      value: toggle,
    })
  }
}

export const loadDeleteDealBuilderSubmissionDetail = (id) => {
  return (dispatch, getState) => {
    dispatch({
      type: 'SET_DELETE_DEAL_BUILDER_ID',
      value: id,
    })
    dispatch(toggleDeleteDealBuilderSubmissionModal(true))
  }
}

export const confirmDeleteBuilderSubmissionDetail = (data) => {
  return (dispatch, getState) => {
    let { url, query } = config.dealBuilder.delete
    const params = convertParamsToDeleteDealBuilderSubmissionData(data)
    dispatch(toggleLoading('delete', true))
    return axios
      .post(
        url,
        {
          query: query,
          variables: params,
        },
        {
          withCredentials: true,
        }
      )
      .then((response) => {
        dispatch({
          type: 'SET_DEAL_BUILDER_LIST',
          value: [],
        })
        dispatch(requestDealBuilderSubmissions())
        dispatch(toggleDeleteDealBuilderSubmissionModal(false))
        dispatch(toggleLoading('delete', false))
      })
      .catch((error) => {
        handleAxiosErrors(error)
        dispatch(toggleLoading('delete', false))
        dispatch(toggleDeleteDealBuilderSubmissionModal(false))
      })
  }
}

export const convertParamsToDeleteDealBuilderSubmissionData = (params) => {
  const { id } = params
  return {
    ID: id,
  }
}

export const removeDealBuilderSubmissionDetailFromList = (id) => {
  return (dispatch, getState) => {
    const store = getState()
    const list = store.dealBuilderSupplier.list
    const newList = list.filter((item, index) => item.ID !== id)
    dispatch({
      type: 'SET_DEAL_BUILDER_LIST',
      value: newList,
    })
  }
}

export const compareDealsBeforeSubmission = () => {
  return (dispatch, getState) => {
    const state = getState()

    let postData = buildDealBuilderSubmissionData(state.dealBuilderSupplier.details)
    console.log('postData', postData)
    dispatch(toggleLoading('form', true))

    return axiosCompareDealBuilderSubmission(postData)
      .then((response) => {
        const { Discounts, PromoDeals } = response?.data?.data?.compareDealDiscount
        const discountsList = JSON.parse(Discounts || '{}')
        const promoDealsList = JSON.parse(PromoDeals || '{}')
        if (Object.keys(discountsList)?.length > 0 || Object.keys(promoDealsList)?.length > 0) {
          const updatedComparedDealBuilderSubmissionDataList = updatedComparedDealBuilderSubmissionData(
            state.dealBuilderSupplier.availableProducts,
            response?.data?.data?.compareDealDiscount
          )
          dispatch(toggleDeleteDealBuilderSubmissionModal(true))
          dispatch({
            type: 'REPLACE_DEAL_BUILDER_AVAILABLE_PRODUCTS',
            value: updatedComparedDealBuilderSubmissionDataList,
          })
        } else {
          dispatch(postDealBuilderSubmission())
        }
      })
      .catch((error) => {
        handleAxiosErrors(error)
      })
      .then(() => {
        dispatch(toggleLoading('form', false))
      })
  }
}

export const postDealBuilderSubmission = () => {
  return async (dispatch, getState) => {
    const state = getState()

    let postData = buildDealBuilderSubmissionData(state.dealBuilderSupplier.details)

    if (window.dealBuilderID) {
      let prevItems = postData['items'] || []
      let updatedItems = postData['Items'] || []
      let removedProductsIds = []

      for (let ele of prevItems) {
        let found = updatedItems.find((obj) => obj?.ProductID == ele?.Product?.ID)
        if (!found) {
          removedProductsIds.push(ele?.Product?.ID)
        }
      }

      postData['RemovedProductsIDs'] = removedProductsIds
    }

    dispatch(toggleLoading('form', true))

    if (state.dealBuilderSupplier.details?.Type === 'Ongoing Deal') {
      const { details, sentryEvents } = state.dealBuilderSupplier
      dispatch(
        captureSentryEvent(
          'Ongoing Deal Builder Submission Tracker - ' + (details?.ID || 'New'),
          'error',
          {
            feature: 'deal-builder',
            page: 'deal-builder',
            deal_id: details?.ID || 0,
          },
          sentryEvents,
          {
            postData: JSON.stringify(postData),
          }
        )
      )
    }

    return axiosCreateDealBuilderSubmission(postData)
      .then((response) => {
        if (response.data.data.createDealBuilderSubmission) {
          window.location.href = '/supplier/deal-builder/'
          dispatch(toggleDeleteDealBuilderSubmissionModal(false))
        } else {
          createAlert({}, 'error')
        }
      })
      .catch((error) => {
        handleAxiosErrors(error)
      })
      .then(() => {
        dispatch(toggleLoading('form', false))
      })
  }
}

/**
 * Function to request deal builder submissions list
 *
 * @param params
 */
export const requestDealBuilderSubmissions = () => {
  return (dispatch, getState) => {
    let { url, query } = config.dealBuilder.list

    dispatch(toggleLoading('list', true))

    return axios
      .get(url, {
        params: {
          query: query,
        },
        withCredentials: true,
      })
      .then((response) => {
        if (response.data.data.readAllDealBuilderSubmission.edges) {
          const list = convertDealBuilderListToReact(response.data.data.readAllDealBuilderSubmission.edges)

          dispatch({
            type: 'SET_DEAL_BUILDER_LIST',
            value: list,
          })
        } else {
          console.log('Invalid response')
        }
      })
      .catch((error) => {
        handleAxiosErrors(error)
      })
      .then(() => {
        dispatch(toggleLoading('list', false))
      })
  }
}

export const constructEmptyData = () => {
  return {
    id: 0,
    title: '',
    description: '',
    discountType: '',
    durationFrom: moment().format('DD/MM/YYYY'),
    durationTo: moment().add(1, 'month').format('DD/MM/YYYY'),
    applyPromotionTo: '',
    buyQuantity: 0,
    getQuantity: 0,
    buyProducts: [],
    getProducts: [],
    buyProduct: 0,
    assignedCustomers: [],
    assignedZones: [],
    enabled: 1,
    caseDiscountType: '',
    caseDiscountValue: '',
    canChangeCaseDiscountType: 1,
    QuantityDependentOffPercentage: 0,
    QuantityDependentOffValue: 0,
  }
}

const axiosRequestEnCustomerDiscounts = (memberID) => {
  const { url, query } = config.products.enCustomerDiscounts

  let variables = {}

  if (memberID) {
    variables['MemberID'] = parseInt(memberID, 10)
  }

  return axios.post(url, {
    query,
    variables,
  })
}

const axiosRefreshEnCustomerDiscounts = (memberID) => {
  const { url, query } = config.products.refreshEnCustomerDiscounts

  let variables = {}

  if (memberID) {
    variables['MemberID'] = parseInt(memberID, 10)
  }

  return axios.post(url, {
    query,
    variables,
  })
}

/**
 * Due to pollingDiscountSyncing function could be triggered multiple times
 * We have to clear the interval
 */
const pollingDiscountSyncing = (productIDs, products, memberID) => {
  return (dispatch, getState) => {
    const state = getState()

    if (window.pollingAxios) {
      clearInterval(window.pollingAxios)
    }

    async function pollingAxios() {
      try {
        const enCustomerDiscountsDataResponse = await axiosRequestEnCustomerDiscounts(memberID)
        const customerPromo = JSON.parse(
          enCustomerDiscountsDataResponse?.data?.data?.readEnCustomerDiscounts?.Discounts
        )

        const discountSyncing =
          !!enCustomerDiscountsDataResponse?.data?.data?.readEnCustomerDiscounts?.IsDiscountSyncing || false

        if (discountSyncing === false) {
          clearInterval(window.pollingAxios)

          dispatch(setDiscountSyncing(false))
          dispatch(
            requestDealBuilderDiscountedProductsPricesWithCustomerPromo(productIDs, products, memberID, customerPromo)
          )
        }
      } catch (error) {
        console.log('polling discount syncing error')
        handleAxiosErrors(error)
      }
    }

    window.pollingAxios = setInterval(pollingAxios, 2000, state)
  }
}

const getCustomerPromo = (productIDs, products, memberID) => {
  return async (dispatch, getState) => {
    let customerPromo

    try {
      const enCustomerDiscountsDataResponse = await axiosRequestEnCustomerDiscounts(memberID)
      customerPromo = JSON.parse(enCustomerDiscountsDataResponse?.data?.data?.readEnCustomerDiscounts?.Discounts)
      const discountSyncing =
        !!enCustomerDiscountsDataResponse?.data?.data?.readEnCustomerDiscounts?.IsDiscountSyncing || true

      dispatch(setDiscountSyncing(true))

      if (discountSyncing === true) {
        dispatch(pollingDiscountSyncing(productIDs, products, memberID))
      }
    } catch (error) {
      handleAxiosErrors(error)
      return
    }

    return customerPromo
  }
}

const requestDealBuilderDiscountedProductsPrices = (list, products, memberID) => {
  return async (dispatch, getState) => {
    const customerPromo = await dispatch(getCustomerPromo(list, products, memberID))

    // Break the request into every 700
    const chunkedList = breakListIntoChunks(list, 700)

    chunkedList.map((chunkedProductIDs) => {
      if (chunkedProductIDs && chunkedProductIDs?.length > 0) {
        axiosRequestProcessedProductsPrices(chunkedProductIDs, memberID)
          .then((response) => {
            const productPrices = convertProductsPricesToAReadableFormat(response.data.data.readProductPrices, false)

            const mergedProducts = _.map(products, function (item) {
              return _.merge(item, _.find(productPrices, { id: item.id }))
            })

            let _products = []
            mergedProducts.forEach((p) => {
              const { id, sku, wetTax, unit_price, case_price, qty_per_case, wholesale_unit_per_case } = p
              let promoListSize = 0
              if (customerPromo) {
                promoListSize = Array.isArray(customerPromo)
                  ? customerPromo?.length
                  : Object.keys(customerPromo)?.length
              }
              // const target = customerPromo[sku]

              if (promoListSize > 0 && customerPromo && customerPromo[sku]) {
                _products.push({
                  BuyableID: id,
                  CasePrice: case_price ? case_price * (1 + wetTax) : 0, // ORIGINAL CASE PRICE
                  UnitPrice: unit_price ? unit_price * (1 + wetTax) : 0, // ORIGINAL UNIT PRICE
                  Discount: customerPromo[sku]['CalculatedDiscount1'] || 0, // CASE DISCOUNT
                  UnitDiscount: customerPromo[sku]['CalculatedUnitDiscount2'] * (1 + wetTax) || 0, // UNIT DISCOUNT
                  DiscountedUnitPrice: unit_price
                    ? ((case_price - customerPromo[sku]['CalculatedCaseDiscount2']) / wholesale_unit_per_case) *
                      (1 + wetTax)
                    : 0, // DISCOUNTED UNIT PRICE
                  DiscountedCasePrice: case_price
                    ? (case_price - customerPromo[sku]['CalculatedCaseDiscount2']) * (1 + wetTax)
                    : 0, // DISCOUNTED CASE PRICE
                  QtyPerCase: qty_per_case,
                  WholesaleUnitsPerCase: wholesale_unit_per_case,
                  WetTax: wetTax,
                  FrontLineCasePrice: case_price ? case_price : 0,
                })
              } else {
                _products.push({
                  BuyableID: id,
                  CasePrice: case_price ? case_price * (1 + wetTax) : 0,
                  UnitPrice: unit_price ? unit_price * (1 + wetTax) : 0,
                  Discount: 0,
                  UnitDiscount: 0,
                  DiscountedUnitPrice: unit_price ? (unit_price / wholesale_unit_per_case) * (1 + wetTax) : 0,
                  DiscountedCasePrice: case_price ? case_price * (1 + wetTax) : 0,
                  QtyPerCase: qty_per_case,
                  WholesaleUnitsPerCase: wholesale_unit_per_case,
                  WetTax: wetTax,
                  FrontLineCasePrice: case_price ? case_price : 0,
                })
              }
            })

            const _redo = convertProductsPricesToAReadableFormat(_products, false)

            // Update prices
            dispatch(updateFullProductsList(_redo))
            dispatch(updateAvailableProducts(_redo))
          })
          .catch((error) => handleAxiosErrors(error))
      }
    })
  }
}

const requestDealBuilderDiscountedProductsPricesWithCustomerPromo = (list, products, memberID, customerPromo) => {
  return async (dispatch, getState) => {
    // Break the request into every 700
    const chunkedList = breakListIntoChunks(list, 700)

    chunkedList.map((chunkedProductIDs) => {
      if (chunkedProductIDs && chunkedProductIDs?.length > 0) {
        axiosRequestProcessedProductsPrices(chunkedProductIDs, memberID)
          .then((response) => {
            const productPrices = convertProductsPricesToAReadableFormat(response.data.data.readProductPrices, false)

            const mergedProducts = _.map(products, function (item) {
              return _.merge(item, _.find(productPrices, { id: item.id }))
            })

            let _products = []
            mergedProducts.forEach((p) => {
              const { id, sku, wetTax, unit_price, case_price, qty_per_case, wholesale_unit_per_case } = p
              if (customerPromo) {
                promoListSize = Array.isArray(customerPromo)
                  ? customerPromo?.length
                  : Object.keys(customerPromo)?.length
              }
              // const target = customerPromo[sku]

              if (promoListSize > 0 && customerPromo && customerPromo[sku]) {
                _products.push({
                  BuyableID: id,
                  CasePrice: case_price ? case_price * (1 + wetTax) : 0, // ORIGINAL CASE PRICE
                  UnitPrice: unit_price ? unit_price * (1 + wetTax) : 0, // ORIGINAL UNIT PRICE
                  Discount: customerPromo[sku]['CalculatedDiscount1'] || 0, // CASE DISCOUNT
                  UnitDiscount: customerPromo[sku]['CalculatedUnitDiscount2'] * (1 + wetTax) || 0, // UNIT DISCOUNT
                  DiscountedUnitPrice: unit_price
                    ? ((case_price - customerPromo[sku]['CalculatedCaseDiscount2']) / wholesale_unit_per_case) *
                      (1 + wetTax)
                    : 0, // DISCOUNTED UNIT PRICE
                  DiscountedCasePrice: case_price
                    ? (case_price - customerPromo[sku]['CalculatedCaseDiscount2']) * (1 + wetTax)
                    : 0, // DISCOUNTED CASE PRICE
                  QtyPerCase: qty_per_case,
                  WholesaleUnitsPerCase: wholesale_unit_per_case,
                  WetTax: wetTax,
                  FrontLineCasePrice: case_price ? case_price : 0,
                })
              } else {
                _products.push({
                  BuyableID: id,
                  CasePrice: case_price ? case_price * (1 + wetTax) : 0,
                  UnitPrice: unit_price ? unit_price * (1 + wetTax) : 0,
                  Discount: 0,
                  UnitDiscount: 0,
                  DiscountedUnitPrice: unit_price ? (unit_price / wholesale_unit_per_case) * (1 + wetTax) : 0,
                  DiscountedCasePrice: case_price ? case_price * (1 + wetTax) : 0,
                  QtyPerCase: qty_per_case,
                  WholesaleUnitsPerCase: wholesale_unit_per_case,
                  WetTax: wetTax,
                  FrontLineCasePrice: case_price ? case_price : 0,
                })
              }
            })

            const _redo = convertProductsPricesToAReadableFormat(_products, false)
            // Update prices
            dispatch(updateFullProductsList(_redo))
            dispatch(updateAvailableProducts(_redo))
          })
          .catch((error) => handleAxiosErrors(error))
      }
    })
  }
}

/**
 * Request products
 *
 * @param params
 * @returns {function(*, *): *}
 */
export const requestProducts = (memberID = 0, zoneID = 0) => {
  return async (dispatch, getState) => {
    const state = getState()
    const params = {
      startingIndex: 0,
      amount: 5000,
      perPage: 9999,
      // WithSuperSupplierBrands: true,
    }
    dispatch(toggleLoading('products', true))
    dispatch(triggerDiscount(memberID))

    return axiosRequestProductList(state, params).then((response) => {
      const warehouseCode = state.dealBuilderSupplier.details?.Customers?.[0]?.original?.Warehouse?.Code

      const products = convertProductsListingToAReadableFormat(
        response.data.data.searchResult.edges,
        state.user,
        warehouseCode
      )
      const productIDs = products.map((item) => item.productID)

      Promise.all([dispatch(replaceAvailableProducts(products))]).then(() => {
        dispatch(requestDiscountedProductsPrices(productIDs, products, memberID, zoneID, false))
      })
    })
  }
}

export const requestProductsEdit = (memberID = 0, zoneID = 0) => {
  return async (dispatch, getState) => {
    const state = getState()
    const params = {
      startingIndex: 0,
      amount: 5000,
      perPage: 9999,
      // WithSuperSupplierBrands: true,
    }
    dispatch(triggerDiscount(memberID))

    return axiosRequestProductList(state, params).then((response) => {
      const warehouseCode = state.dealBuilderSupplier.details?.Customers?.[0]?.original?.Warehouse?.Code

      const products = convertProductsListingToAReadableFormat(
        response.data.data.searchResult.edges,
        state.user,
        warehouseCode
      )
      const ProductsIDs = products.map((product) => product.id)

      Promise.all([dispatch(replaceAvailableProducts(products))]).then(() => {
        Promise.all([dispatch(requestDiscountedDealBuilderProductsPrices(ProductsIDs, products, memberID, zoneID))])
      })
    })
  }
}

export const convertDealBuilderProductsForEdit = (availableProducts, dealBuilderItems) => {
  const ProductsIDs = availableProducts.map((product) => product.id)
  const productItemIDs =
    dealBuilderItems && dealBuilderItems?.length > 0
      ? getProductIDsFromDealBuilderSubmission(dealBuilderItems)
      : ProductsIDs
  const Products = availableProducts.filter((pro) => productItemIDs.includes(pro.id))
  console.log('Deal Builder Item Products', getProductIDsFromDealBuilderSubmission(dealBuilderItems), Products)
  return Products
}

export const getProductIDsFromDealBuilderSubmission = (items) => {
  return items.map((item) => {
    return item?.Product?.ID
  })
}

export const validateDealBuilderDetails = (data) => {
  const { canChangeDiscountType, Type } = data
  let validationStatus
  let standardRequiredFields = ['Title', 'FromDate', 'ToDate', 'AppliesTo', 'Type']

  // Standard Required Fields
  validationStatus = checkEmptyOnList(data, standardRequiredFields)

  if (data.AppliesTo == 'Customer') {
    if (data.Customers?.length == 0 || data?.Customers == undefined) {
      validationStatus = false
    }
  }
  if (data.AppliesTo == 'Customer Group') {
    if (data.Zones?.length == 0 || data?.Zones == undefined) {
      validationStatus = false
    }
  }

  if (validationStatus && canChangeDiscountType) {
    if (!Type) {
      validationStatus = false
    }
  }

  if (validationStatus) {
    if (Type == 'Bonus') {
      // Check: Discount Type Bonus
      let requiredFields = ['BuyProducts', 'GetProducts', 'BuyQty', 'GetQty']

      validationStatus = checkEmptyOnList(data, requiredFields)

      if (data.BuyQty <= 0 || data.GetQty <= 0) {
        validationStatus = false
      }
    } else if (Type == 'Ongoing Deal') {
      // Check: Ongoing Deal
      let modifiedValueArray = data?.modifiedRows !== undefined ? Object.values(data?.modifiedRows) : []
      let anyModified =
        modifiedValueArray?.length > 0 ? modifiedValueArray.some((item) => item.modified == true) : false
      // console.log('Ongoing Deal modifiedValueArray anyModified');
      // console.log({modifiedValueArray , anyModified});
      if (!window.dealBuilderID && !anyModified) {
        validationStatus = false
      }
    } else if (Type == 'Case Discount') {
      // Check: Discount Type Case Discount
      let requiredFields = ['buyProduct', 'caseDiscountType', 'caseDiscountValue']

      validationStatus = checkEmptyOnList(data, requiredFields)
    } else if (Type == 'Quantity Dependent Deal') {
      // Check: Discount Type Bonus
      let requiredFields = ['BuyProducts', 'BuyQty']

      validationStatus = checkEmptyOnList(data, requiredFields)

      if (data.BuyQty <= 0) {
        validationStatus = false
      } else {
        if (data.QuantityDependentOffValue <= 0 && data.QuantityDependentOffPercentage <= 0) {
          validationStatus = false
        }
      }
    }
  }

  return validationStatus
}

/**
 * Axios to create promotion
 *
 * @param params
 * @returns {AxiosPromise<any>}
 */
export const axiosCreateDealBuilderSubmission = (params) => {
  let { url, query } = config.dealBuilder.create

  return axios.post(
    url,
    {
      query: query,
      variables: params,
    },
    {
      // withCredentials: true
    }
  )
}

export const axiosCompareDealBuilderSubmission = (params) => {
  let { url, query } = config.dealBuilder.compare

  return axios.post(
    url,
    {
      query: query,
      variables: params,
    },
    {
      // withCredentials: true
    }
  )
}

export const valueToFixed = (value) => {
  value = returnvalidNumber(value)
  return value.toFixed(2)
}
export const returnvalidNumber = (value) => {
  return !isNaN(value) ? parseFloat(value) : 0
}

export const returnValidPercentageValue = (value) => {
  return value > 100 ? 100 : value
}

export const requestDealBuilder = (ID) => {
  return (dispatch, getState) => {
    return axiosrequestDealBuilderSubmission({ ID: ID })
      .then((response) => {
        const { readOneDealBuilderSubmission } = response.data.data
        dispatch(setDealBuilderSubmission(readOneDealBuilderSubmission))
      })
      .catch((error) => {
        handleAxiosErrors(error)
      })
      .then((response) => {
        dispatch(toggleLoading('detail', false))
      })
  }
}

export const axiosrequestDealBuilderSubmission = (params) => {
  let { url, query } = config.dealBuilder.detail
  const { ID } = params
  query = query.replace(/{dealBuilderID}/, ID)

  return axios.get(url, {
    params: {
      query,
    },
    withCredentials: true,
  })
}

export const setDealBuilderSubmission = (dealBuilderSubmission) => {
  return (dispatch, getState) => {
    if (dealBuilderSubmission?.Items?.edges && dealBuilderSubmission.Items.edges.length > 0) {
      dispatch(convertItemsToReactSelect(convertEdgesToReact(dealBuilderSubmission.Items.edges)))
    }
    dispatch(updateDealBuilderDetails('Type', dealBuilderSubmission.Type))
    dispatch(updateDealBuilderDetails('LimitPerCustomer', dealBuilderSubmission.LimitPerCustomer))
    if (dealBuilderSubmission?.Customers?.edges && dealBuilderSubmission.Customers.edges.length > 0) {
      dispatch(updateDealBuilderDetails('AppliesTo', 'Customer'))
      dispatch(
        updateDealBuilderDetails(
          'Customers',
          convertMemberNamesToReactSelect(convertEdgesToReact(dealBuilderSubmission.Customers.edges))
        )
      )
    } else if (dealBuilderSubmission?.PromotionZones?.edges && dealBuilderSubmission.PromotionZones.edges.length > 0) {
      dispatch(updateDealBuilderDetails('AppliesTo', 'Customer Group'))
      dispatch(updateDealBuilderDetails('Zones', convertZonesToReactSelect(dealBuilderSubmission.PromotionZones.edges)))
    }

    if (dealBuilderSubmission?.BuyProducts?.edges && dealBuilderSubmission.BuyProducts.edges.length > 0) {
      dispatch(updateDealBuilderDetails('BuyProducts', getProductIDs(dealBuilderSubmission.BuyProducts.edges)))
    }
    if (dealBuilderSubmission?.GetProducts?.edges && dealBuilderSubmission.GetProducts.edges.length > 0) {
      dispatch(updateDealBuilderDetails('GetProducts', getProductIDs(dealBuilderSubmission.GetProducts.edges)))
    }
    dispatch(updateDealBuilderDetails('BuyQty', dealBuilderSubmission.BuyQty))
    dispatch(updateDealBuilderDetails('FromDate', formatDateToReact(dealBuilderSubmission.FromDate)))
    dispatch(updateDealBuilderDetails('ToDate', formatDateToReact(dealBuilderSubmission.ToDate)))
    dispatch(updateDealBuilderDetails('GetQty', dealBuilderSubmission.GetQty))
    dispatch(updateDealBuilderDetails('ID', dealBuilderSubmission.ID))
    dispatch(
      updateDealBuilderDetails(
        'QuantityDependentOffPercentage',
        parseFloat(dealBuilderSubmission.QuantityDependentOffPercentage).toFixed(2)
      )
    )
    dispatch(
      updateDealBuilderDetails(
        'QuantityDependentOffValue',
        parseFloat(dealBuilderSubmission.QuantityDependentOffValue).toFixed(2)
      )
    )
    dispatch(updateDealBuilderDetails('Title', dealBuilderSubmission.Title))
    dispatch(setDealBuilderCompareableDetail())
  }
}

export const formatDateToReact = (date) => {
  return moment(date).format('DD/MM/YYYY')
}

export const getProductIDs = (products) => {
  if (products.length === 0) return []
  return products.map((product) => {
    return product.node.ID
  })
}

export const convertEdgesToReact = (items) => {
  return items.map((item) => {
    return item.node
  })
}

export const convertItemsToReactSelect = (items) => {
  return (dispatch, getState) => {
    const store = getState()
    dispatch(updateDealBuilderDetails('items', items))
    // setTimeout(()=>{
    // dispatch(setItemsToDealBuilderProductTable(items));
    // },7000);
  }
}

export const setItemsToDealBuilderProductTable = (items) => {
  return (dispatch, getState) => {
    const store = getState()
    let supplierProducts = store.productsSupplier?.list || {}
    items.forEach((item) => {
      const { Amount, Product } = item
      if (typeof supplierProducts[Product.ID] !== 'undefined') {
        let product = supplierProducts[Product.ID]
        product = {
          ...product,
          productID: Product.ID,
          sell_by_case: Product.SellByPackageOnly || false,
          qty_per_case: Product.ItemsInCase,
          wholesale_unit_per_case: Product.WholesaleUnitsPerCase,
          wetTax: Product.WetTax ? +Product.WetTax : product.wet_tax,
        }

        dispatch(updateDealBuilderModifiedRow({ original: product }, 'flat_discount', parseFloat(Amount).toFixed(2), 0))
      }
    })
  }
}

export const requestDiscountedDealBuilderProductsPrices = (list, products, memberID, zoneID = 0) => {
  return async (dispatch, getState) => {
    const store = getState()
    await axiosRefreshEnCustomerDiscounts(memberID)
    const customerPromo = await dispatch(getSupplierCustomerPromo(list, products, memberID, zoneID))
    const enCustomerDiscountsDataResponse = await axiosRequestEnCustomerDiscounts(store, memberID)
    const discountSyncing = !!enCustomerDiscountsDataResponse?.data?.data?.readEnCustomerDiscounts?.IsDiscountSyncing
    // Break the request into every 700

    if (!discountSyncing) {
      const chunkedList = breakListIntoChunks(list, 700)

      chunkedList.map((chunkedProductIDs) => {
        if (chunkedProductIDs && chunkedProductIDs.length > 0) {
          axiosRequestProcessedProductsPrices(chunkedProductIDs, memberID, zoneID)
            .then((response) => {
              const productPrices = convertProductsPricesToAReadableFormat(response.data.data.readProductPrices, false)

              const mergedProducts = _.map(products, function (item) {
                return _.merge(item, _.find(productPrices, { id: item.id }))
              })

              let _products = []
              mergedProducts.forEach((p) => {
                const { id, sku, wetTax, unit_price, case_price, qty_per_case, wholesale_unit_per_case } = p
                const target = customerPromo[sku]

                if (target) {
                  _products.push({
                    BuyableID: id,
                    CasePrice: case_price ? case_price * (1 + wetTax) : 0, // ORIGINAL CASE PRICE
                    UnitPrice: unit_price ? unit_price * (1 + wetTax) : 0, // ORIGINAL UNIT PRICE
                    Discount: target['CalculatedDiscount1'] || 0, // CASE DISCOUNT
                    UnitDiscount: target['CalculatedUnitDiscount2'] * (1 + wetTax) || 0, // UNIT DISCOUNT
                    DiscountedUnitPrice: unit_price
                      ? ((case_price - target['CalculatedCaseDiscount2']) / wholesale_unit_per_case) * (1 + wetTax)
                      : 0, // DISCOUNTED UNIT PRICE
                    DiscountedCasePrice: case_price
                      ? (case_price - target['CalculatedCaseDiscount2']) * (1 + wetTax)
                      : 0, // DISCOUNTED CASE PRICE
                    QtyPerCase: qty_per_case,
                    WholesaleUnitsPerCase: wholesale_unit_per_case,
                    WetTax: wetTax,
                    FrontLineCasePrice: case_price ? case_price : 0,
                  })
                } else {
                  _products.push({
                    BuyableID: id,
                    CasePrice: case_price ? case_price * (1 + wetTax) : 0,
                    UnitPrice: unit_price ? unit_price * (1 + wetTax) : 0,
                    Discount: 0,
                    UnitDiscount: 0,
                    DiscountedUnitPrice: unit_price ? (unit_price / wholesale_unit_per_case) * (1 + wetTax) : 0,
                    DiscountedCasePrice: case_price ? case_price * (1 + wetTax) : 0,
                    QtyPerCase: qty_per_case,
                    WholesaleUnitsPerCase: wholesale_unit_per_case,
                    WetTax: wetTax,
                    FrontLineCasePrice: case_price ? case_price : 0,
                  })
                }
              })

              const _redo = convertSupplierProductsPricesToAReadableFormat(_products, false)

              // Update prices
              dispatch(updateFullProductsList(_redo))
              dispatch(updateAvailableProducts(_redo))
              dispatch(setItemsToDealBuilderProductTable(store.dealBuilderSupplier?.details?.items || []))
              dispatch(setDiscountSyncing(false))
            })
            .catch((error) => handleAxiosErrors(error))
            .then((response) => dispatch(toggleLoading('products', false)))
        }
      })
    }
  }
}

function convertSupplierProductsPricesToAReadableFormat(data, ignoreDiscount = false, isSaleQuote = false) {
  if (!data || data.length === 0) {
    return []
  }

  return data.map((discountData) => {
    let {
      Discount,
      BuyableID,
      UnitPrice,
      CasePrice,
      DiscountedCasePrice,
      DiscountedUnitPrice,
      UnitDiscount = 0,
      QtyPerCase = 1,
      WholesaleUnitsPerCase = 1,
      SupplierDiscountPerCase = 0,
      ParamountDiscount = 0,
      WetTax = 0,
      FrontLineCasePrice = 0,
      Featured = 0,
      unitSurcharge = 0,
    } = discountData

    CasePrice = floorToTwoDecimals(CasePrice)
    UnitPrice = floorToTwoDecimals(UnitPrice)
    DiscountedCasePrice = floorToTwoDecimals(DiscountedCasePrice)
    DiscountedUnitPrice = floorToTwoDecimals(DiscountedUnitPrice)

    if (ignoreDiscount === true) {
      return {
        id: String(BuyableID),
        case_discount: 0,
        case_price: floorToTwoDecimals(CasePrice),
        case_price_tax: 0,
        unit_discount: 0,
        unit_price: floorToTwoDecimals(UnitPrice),
        unit_price_tax: 0,
        hasDiscount: false,
      }
    } else {
      /* Calculate Surcharge on unit price (Ex Wet)*/
      let wetExclCasePrice = DiscountedCasePrice / (1 + WetTax)
      let wetInclUnitPrice = (wetExclCasePrice / WholesaleUnitsPerCase) * (1 + unitSurcharge) * (1 + WetTax)

      const caseDiscount = Discount

      const unitDiscount = UnitDiscount || 0
      const hasDiscount = caseDiscount > 0 || unitDiscount > 0

      let unit_price = UnitPrice
      if (DiscountedUnitPrice) {
        unit_price = floorToTwoDecimals((DiscountedCasePrice - caseDiscount * (1 + WetTax)) / WholesaleUnitsPerCase)
      }

      // const discounted_case_price = DiscountedCasePrice - caseDiscount * (1 + WetTax)
      // const cal_case_price = CasePrice - caseDiscount * (1 + WetTax)
      const discounted_case_price = DiscountedCasePrice - caseDiscount * (1 + WetTax) * (1 + 0)
      const discounted_unit_price = (discounted_case_price / WholesaleUnitsPerCase) * (1 + unitSurcharge)

      const cal_case_price = CasePrice - caseDiscount * (1 + 0)
      const cal_unit_price = (cal_case_price / WholesaleUnitsPerCase) * (1 + unitSurcharge)

      return {
        id: String(BuyableID),
        case_discount: floorToTwoDecimals(caseDiscount),
        supplier_discount_per_case: parseFloat(SupplierDiscountPerCase).toFixed(2),
        paramount_discount: parseFloat(ParamountDiscount).toFixed(2),
        // wet_tax: 0, // floorToTwoDecimals(0),
        wet_tax: isSaleQuote && WetTax ? parseFloat(WetTax) : 0,
        case_price: floorToTwoDecimals(DiscountedCasePrice),
        case_price_tax: 0,
        unit_discount: unitDiscount,
        unit_price: floorToTwoDecimals(wetInclUnitPrice),
        unit_price_tax: 0,
        hasDiscount: hasDiscount,
        hasFeaturedDiscount: hasDiscount && Featured,
        discounted_case_price: floorToTwoDecimals(discounted_case_price),
        discounted_unit_price: floorToTwoDecimals(discounted_unit_price),
        frontline_case_price: floorToTwoDecimals(FrontLineCasePrice),
        cal_case_price: floorToTwoDecimals(cal_case_price),
        cal_unit_price: floorToTwoDecimals(cal_unit_price),
        unitSurcharge: unitSurcharge,
      }
    }
  })

  return newList
}

export const setDealBuilderCompareableDetail = () => {
  return (dispatch, getState) => {
    const state = getState()
    const { details = null, compareDetails = null } = state?.dealBuilderSupplier
    dispatch({
      type: 'SET_DEAL_BUILDER_COMPARE_DETAILS',
      value: details,
    })
  }
}
export function validatedDealEdit(details, compareDetails) {
  let edited = false
  comparableValues.forEach((key) => {
    if (edited == false && compareValues(key, details, compareDetails)) {
      edited = true
    }
  })

  if (edited == false && details?.AppliesTo == 'Customer' && details?.Customers?.length > 0) {
    const detailCustomers = details?.Customers?.map((customer) => customer.value)
    const compareCustomers = compareDetails?.Customers?.map((customer) => customer.value)
    if (detailCustomers.sort().join(',') != compareCustomers.sort().join(',')) {
      edited = true
    }
  }

  if (edited == false && details?.AppliesTo == 'Customer Group' && details?.Zones?.length > 0) {
    const detailZones = details?.Zones?.map((zone) => zone.value)
    const compareZones = compareDetails?.Zones?.map((zone) => zone.value)
    if (detailZones.sort().join(',') != compareZones.sort().join(',')) {
      edited = true
    }
  }

  if (edited == false && details?.Type !== 'Ongoing Deal' && details?.BuyProducts?.length > 0) {
    if (details?.BuyProducts.sort().join(',') != compareDetails?.BuyProducts.sort().join(',')) {
      edited = true
    }
  }

  if (edited == false && details?.Type !== 'Ongoing Deal' && details?.GetProducts?.length > 0) {
    if (details?.GetProducts.sort().join(',') != compareDetails?.GetProducts.sort().join(',')) {
      edited = true
    }
  }

  if (edited == false && compareDetails?.items?.length > 0) {
    compareDetails?.items?.forEach((item) => {
      const { ID } = item?.Product
      const { Amount } = item
      if (
        edited === false &&
        parseFloat(details?.modifiedRows?.[ID]?.flatDiscountValue).toFixed(2) != parseFloat(Amount).toFixed(2)
      ) {
        edited = true
      }
    })
  } else if (
    compareDetails?.items?.length == 0 &&
    details?.modifiedRows &&
    Object.keys(details?.modifiedRows).length > 0
  ) {
    edited = true
  }

  /*
    Enable save button if new product is added on edit deal
  */
  if (
    edited == false &&
    details?.Type == 'Ongoing Deal' &&
    details?.modifiedRows &&
    Object.keys(details?.modifiedRows)?.length !== compareDetails?.items?.length
  ) {
    let uniqueRows = getUniqueDealRows(details?.modifiedRows, compareDetails?.items)
    if (onGoingDealHasDiscount(uniqueRows)) {
      edited = true
    }
  }

  /*
    Disable save button if all products on edit deal has 0 discount
  */
  if (edited == true && details?.Type == 'Ongoing Deal' && !onGoingDealHasDiscount(details?.modifiedRows)) {
    edited = false
  }

  return edited
}

export const getUniqueDealRows = (rows = [], compareRows = []) => {
  let newRows = {}

  for (let [key, value] of Object.entries(rows)) {
    if (!compareRows?.some((item) => key == item?.Product?.ID)) {
      newRows[key] = value
    }
  }

  return newRows
}

export const onGoingDealHasDiscount = (rows) => {
  let hasDiscount = false
  if (rows && typeof rows == 'object') {
    rows = Object.keys(rows).map((key) => rows?.[key])
    rows.forEach((row) => {
      if (hasDiscount === false && row?.flatDiscountValue > 0) {
        hasDiscount = true
      }
    })
  }
  return hasDiscount
}

export const onGoingDealHasZeroDiscount = (rows) => {
  let hasZero = false
  if (rows && typeof rows == 'object') {
    rows = Object.keys(rows).map((key) => rows?.[key])
    rows.forEach((row) => {
      if (hasZero === false && row?.flatDiscountValue == 0) {
        hasZero = true
      }
    })
  }
  return hasZero
}

export const compareValues = (key, details, compareDetails) => {
  key = key.split('.')
  if (typeof details?.[key] !== 'array' && typeof details?.[key] !== 'object') {
    return details?.[key?.[0]] !== compareDetails?.[key?.[0]]
  }
}

export const comparableValues = [
  'Title',
  'LimitPerCustomer',
  'AppliesTo',
  'FromDate',
  'ToDate',
  'Type',
  'BuyQty',
  'GetQty',
  'QuantityDependentOffPercentage',
  'QuantityDependentOffValue',
  'Type',
  'applyPromotionTo',
  'buyProduct',
  'buyQuantity',
  'canChangeCaseDiscountType',
  'caseDiscountType',
  'caseDiscountValue',
  'description',
  'discountType',
  'getQuantity',
]

export const getMinimumProductPriceValue = (products) => {
  let minimumProductPrice = 0
  if (products.length > 0) {
    minimumProductPrice = products?.[0]?.case_price
    products.forEach((product) => {
      if (minimumProductPrice > product.case_price) {
        minimumProductPrice = product.case_price
      }
    })
  }
  return minimumProductPrice
}

export const getProductDetailByIds = (productIds = [], products) => {
  productIds = productIds.map(String)
  return products.filter((product) => productIds.includes(product.id))
}
